// eslint-disable-next-line no-restricted-imports
import {useChatState, useChatStateLens} from '@github-ui/copilot-chat/CopilotChatContext'
import {useNavigableRef} from '@github-ui/navigable-ref'
import type {Observable} from '@github-ui/observable'
import {useObservableValue, useObservedState} from '@github-ui/react-observable'
import {usePreviousValue} from '@github-ui/use-previous-value'
import {getSessionStorageItemsForKeyPrefix} from '@github-ui/use-safe-storage/session-storage'
import {useStableCallback} from '@github-ui/use-stable-callback'
import {createContext, use, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useBooleanState} from '../../hooks/use-boolean-state'
import {
  areDraftIssuesEqual,
  type ContentPreviewContext as ContentPreviewContextType,
  type DraftIssue,
  type DraftOrExistingIssue,
  type ItemsMap,
  type PreviewableContent,
  type PreviewableContentIdentifier,
  stripVersionFromId,
} from './content-preview-types'
import {useSyncEditedItemsWithCurrentReferences} from './use-sync-items-with-current-references'

export type ContentPreviewContext = ContentPreviewContextType
type ContentPreviewContextInternal = ContentPreviewContext & {
  isContentPreviewOpen: Observable<boolean>
  isResourcePanelOpen: Observable<boolean>
}
export const ContentPreviewContext = createContext<ContentPreviewContextInternal | undefined>(undefined)

/**
 * Stable context containing only callbacks and imperative getters.
 * Never changes identity, so consumers that only need actions won't re-render
 * when items/openItems/selectedItem change.
 */
export type ContentPreviewActions = {
  getItems: () => ItemsMap
  getVersionedItems: () => Map<PreviewableContentIdentifier, PreviewableContentIdentifier[]>
  getOpenItems: ContentPreviewContext['getOpenItems']
  getOpenItemsBeforeSubthreadChange: () => readonly PreviewableContentIdentifier[]
  updateItem: ContentPreviewContext['updateItem']
  openItem: ContentPreviewContext['openItem']
  closeItem: ContentPreviewContext['closeItem']
  closeItems: ContentPreviewContext['closeItems']
  removeItems: ContentPreviewContext['removeItems']
  openPreviewPane: ContentPreviewContext['openPreviewPane']
  closePreviewPane: ContentPreviewContext['closePreviewPane']
  openResourcePanel: ContentPreviewContext['openResourcePanel']
  closeResourcePanel: ContentPreviewContext['closeResourcePanel']
  enableLoadingState: ContentPreviewContext['enableLoadingState']
  disableLoadingState: ContentPreviewContext['disableLoadingState']
  setOpenItemsBeforeSubthreadChange: ContentPreviewContext['setOpenItemsBeforeSubthreadChange']
  contentPreviewRef: ContentPreviewContext['contentPreviewRef']
  returnFocusElementRef: ContentPreviewContext['returnFocusElementRef']
}
const ContentPreviewActionsContext = createContext<ContentPreviewActions | undefined>(undefined)

const emptyItems: ItemsMap = new Map()
const emptyOpenItems: ReturnType<ContentPreviewContext['getOpenItems']> = []

export function ContentPreviewProvider({children}: {children: React.ReactNode}) {
  const isContentPreviewOpen = useObservableValue(false)
  const isResourcePanelOpen = useObservableValue(false)
  const contentPreviewRef = useNavigableRef<HTMLDivElement>()
  const returnFocusElementRef = useRef<HTMLElement | null>(null)
  const [items, setItems] = useState(emptyItems)
  const [openItems, setOpenItems] = useState(emptyOpenItems)
  const openItemsRef = useRef(openItems)
  // eslint-disable-next-line react-hooks/refs
  openItemsRef.current = openItems
  const [openItemsBeforeSubthreadChange, setOpenItemsBeforeSubthreadChange] = useState(emptyOpenItems)
  const [selectedItem, setSelectedItem] = useState<PreviewableContentIdentifier | undefined>(undefined)
  const [showLoadingState, enableLoadingState, disableLoadingState] = useBooleanState(false)
  const [ephemeralTab, setEphemeralTab] = useState<PreviewableContentIdentifier | undefined>(undefined)
  const [resourcePanelRepoId, setResourcePanelRepoId] = useState<{
    repoId: number
    ref?: string
  } | null>(null)
  const [resourcePanelExpandedPath, setResourcePanelExpandedPath] = useState('')
  const [itemSelectionVersion, setItemSelectionVersion] = useState(0)

  const previousThreadId = usePreviousValue(useChatStateLens(s => s.selectedThreadID))
  const threadId = useChatStateLens(s => s.selectedThreadID)
  useEffect(() => {
    if (!previousThreadId || previousThreadId === threadId) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setItems(emptyItems)
    setOpenItems(emptyOpenItems)
    setSelectedItem(undefined)
    setEphemeralTab(undefined)
    setResourcePanelRepoId(null)
    setResourcePanelExpandedPath('')
    setItemSelectionVersion(0)
    if (threadId === null) isContentPreviewOpen.setValue(false)
  }, [threadId, previousThreadId, isContentPreviewOpen, setResourcePanelRepoId, setItemSelectionVersion])

  const openItem = useCallback((id: PreviewableContentIdentifier, selectItem: boolean = true) => {
    setOpenItems(prevVisibleItems => {
      // Item already visible, no-op
      if (prevVisibleItems.includes(id)) return prevVisibleItems

      // Only one version of an item can ever be open
      const unversionedId = stripVersionFromId(id)
      const replaceIndex = prevVisibleItems.findIndex(visibleItem => stripVersionFromId(visibleItem) === unversionedId)

      const newVisibleItems = [...prevVisibleItems]

      if (replaceIndex === -1) newVisibleItems.push(id)
      else newVisibleItems.splice(replaceIndex, 1, id)

      return newVisibleItems
    })

    setSelectedItem(prevSelectedItem =>
      // If we open a different version of the currently selected item, we must update the selected item or nothing will be selected
      selectItem || prevSelectedItem === undefined || stripVersionFromId(prevSelectedItem) === stripVersionFromId(id)
        ? id
        : prevSelectedItem,
    )
    setItemSelectionVersion(v => v + 1)
  }, [])

  const openEphemeralItem = useCallback(
    (id: PreviewableContentIdentifier) => {
      // If the item is already open (e.g. it was promoted to permanent), just select it
      // without closing the current ephemeral tab or re-marking it as ephemeral.
      if (openItemsRef.current.includes(id)) {
        openItem(id)
        return
      }

      setEphemeralTab(prevEphemeral => {
        if (prevEphemeral && prevEphemeral !== id) {
          // Close the previous ephemeral tab
          setOpenItems(prev => {
            const idx = prev.indexOf(prevEphemeral)
            if (idx === -1) return prev
            const next = [...prev]
            next.splice(idx, 1)
            return next
          })
        }
        return id
      })
      openItem(id)
    },
    [openItem],
  )

  const promoteTab = useCallback((id: PreviewableContentIdentifier) => {
    setEphemeralTab(prev => (prev === id ? undefined : prev))
    setItemSelectionVersion(v => v + 1)
  }, [])

  const updateItem = useCallback((item: PreviewableContent) => {
    setItems(prevItems => {
      const isStreaming = 'isStreaming' in item ? item.isStreaming : false
      let duplicateOfId: PreviewableContentIdentifier | undefined

      // For draft issues, check for duplicates once streaming is complete
      if (!isStreaming && (item.type === 'new-issue' || item.type === 'update-issue')) {
        const unversionedId = stripVersionFromId(item.id)

        for (const [existingId, existingItem] of prevItems) {
          if (
            existingId !== item.id && // Don't compare an item with itself
            stripVersionFromId(existingId) === unversionedId &&
            (existingItem.type === 'new-issue' || existingItem.type === 'update-issue') &&
            // Don't consider items that are themselves duplicates - they'll be filtered out of versionedItems
            !existingItem.duplicateOf
          ) {
            if (areDraftIssuesEqual(item, existingItem)) {
              // Because we wait until streaming is complete to check for duplicates, the item may already exist.
              // To prevent unnecessary/infinite updates, we return early if the item hasn't changed and is already
              // marked as a dupe.
              const currentItemInPrevItems = prevItems.get(item.id)
              if (
                currentItemInPrevItems && // does the item already exist?
                areDraftIssuesEqual(item, currentItemInPrevItems as DraftIssue) && // is it still the same as the existing content?
                'duplicateOf' in currentItemInPrevItems &&
                currentItemInPrevItems.duplicateOf === existingId // is it already marked as a duplicate?
              ) {
                return prevItems
              }

              duplicateOfId = existingId
              break
            }
          }
        }
      }

      const newItems = new Map(prevItems)
      if (duplicateOfId && (item.type === 'new-issue' || item.type === 'update-issue')) {
        newItems.set(item.id, {...item, duplicateOf: duplicateOfId} as DraftOrExistingIssue)
      } else {
        newItems.set(item.id, item)
      }
      return newItems
    })
  }, [])

  const closeItem = useStableCallback((id: PreviewableContentIdentifier) => {
    const index = openItems.indexOf(id)
    if (index === -1) return // Item not open, no-op
    const newOpenItems = openItems.toSpliced(index, 1)
    if (selectedItem === id) setSelectedItem(newOpenItems[index - 1] ?? newOpenItems[0])
    setOpenItems(newOpenItems)
    if (ephemeralTab === id) setEphemeralTab(undefined)
  })

  const closeItems = useStableCallback((ids?: PreviewableContentIdentifier[]) => {
    if (ids) {
      let newOpenItems = openItems
      for (const id of ids) {
        const index = newOpenItems.indexOf(id)
        if (index === -1) continue // Item not open, no-op
        newOpenItems = newOpenItems.toSpliced(index, 1)
        if (selectedItem === id) {
          setSelectedItem(newOpenItems[index - 1] ?? newOpenItems[0])
        }
      }
      setOpenItems(newOpenItems)
    } else {
      setOpenItems(emptyOpenItems)
      setSelectedItem(undefined)
      setEphemeralTab(undefined)
    }
  })

  const versionedItems = useMemo(() => {
    const result = new Map<PreviewableContentIdentifier, PreviewableContentIdentifier[]>()

    // collect version sets
    for (const [versionedId, item] of items) {
      const unversionedId = stripVersionFromId(versionedId)
      const versions = result.get(unversionedId) ?? []

      // Skip items marked as duplicates to prevent version bumping
      if ('duplicateOf' in item && item.duplicateOf) {
        continue
      }

      // Skip streaming items to prevent version strobing while content is being generated
      // Once streaming completes, the item will either be marked as a duplicate or appear as a new version
      if ('isStreaming' in item && item.isStreaming) {
        continue
      }

      versions.push(item.id)
      result.set(unversionedId, versions)
    }

    // sort each set
    for (const versions of result.values())
      versions.sort((idA, idB) => {
        const a = items.get(idA)!
        const b = items.get(idB)!
        return 'timestamp' in a && 'timestamp' in b ? a.timestamp.valueOf() - b.timestamp.valueOf() : 0
      })

    return result
  }, [items])

  const removeItems = useStableCallback((ids?: PreviewableContentIdentifier[]) => {
    if (ids) {
      for (const id of ids) {
        setItems(prevItems => {
          const newItems = new Map(prevItems)
          // If the item was not found, return the previous items
          return newItems.delete(id) ? newItems : prevItems
        })

        // If this item is versioned and open, roll the user to the previous version. Otherwise, close the item
        if (openItems.includes(id)) {
          const unversionedId = stripVersionFromId(id)
          const versions = versionedItems.get(unversionedId)
          const versionIndex = versions?.indexOf(id) ?? -1
          const previousVersion = versions?.[versionIndex - 1]
          if (previousVersion) openItem(previousVersion, false)
          else closeItem(id)
        }
      }
    } else {
      setItems(emptyItems)
      setOpenItems(emptyOpenItems)
      setSelectedItem(undefined)
    }
  })

  const getOpenItems = useStableCallback(() => (isContentPreviewOpen.value ? openItems : emptyOpenItems))
  const getItems = useStableCallback(() => items)
  const getVersionedItems = useStableCallback(() => versionedItems)
  const getOpenItemsBeforeSubthreadChange = useStableCallback(() => openItemsBeforeSubthreadChange)
  const openPreviewPane = useCallback(
    (newReturnFocusElement?: HTMLElement | null) => {
      isContentPreviewOpen.setValue(true)
      // Move focus to the workbench dialog so keyboard users land inside it even if it was already visible.
      queueMicrotask(() => {
        contentPreviewRef.current?.focus()
      })
      // Store the element that should receive focus when the pane closes
      // Do this after queuing the focus to avoid state update interference
      if (newReturnFocusElement !== undefined) {
        returnFocusElementRef.current = newReturnFocusElement
      }
    },
    [isContentPreviewOpen, contentPreviewRef, returnFocusElementRef],
  )
  const closePreviewPane = useCallback(() => {
    isContentPreviewOpen.setValue(false)
    isResourcePanelOpen.setValue(false)
  }, [isContentPreviewOpen, isResourcePanelOpen])
  const openResourcePanel = useCallback(() => isResourcePanelOpen.setValue(true), [isResourcePanelOpen])
  const closeResourcePanel = useCallback(() => isResourcePanelOpen.setValue(false), [isResourcePanelOpen])

  const value = useMemo(
    (): ContentPreviewContextInternal => ({
      items,
      versionedItems,
      contentPreviewRef,
      isContentPreviewOpen,
      isResourcePanelOpen,
      getOpenItems,
      allOpenItems: openItems,
      openItemsBeforeSubthreadChange,
      selectedItem,
      updateItem,
      setOpenItemsBeforeSubthreadChange,
      openItem,
      closeItem,
      closeItems,
      openPreviewPane,
      closePreviewPane,
      openResourcePanel,
      closeResourcePanel,
      resourcePanelRepoId,
      setResourcePanelRepoId,
      resourcePanelExpandedPath,
      setResourcePanelExpandedPath,
      removeItems,
      showLoadingState,
      enableLoadingState,
      disableLoadingState,
      returnFocusElementRef,
      ephemeralTab,
      openEphemeralItem,
      promoteTab,
      itemSelectionVersion,
    }),
    [
      items,
      versionedItems,
      contentPreviewRef,
      isContentPreviewOpen,
      isResourcePanelOpen,
      getOpenItems,
      openItems,
      openItemsBeforeSubthreadChange,
      selectedItem,
      updateItem,
      openItem,
      closeItem,
      closeItems,
      openPreviewPane,
      closePreviewPane,
      openResourcePanel,
      closeResourcePanel,
      resourcePanelRepoId,
      setResourcePanelRepoId,
      resourcePanelExpandedPath,
      setResourcePanelExpandedPath,
      removeItems,
      showLoadingState,
      enableLoadingState,
      disableLoadingState,
      returnFocusElementRef,
      ephemeralTab,
      openEphemeralItem,
      promoteTab,
      itemSelectionVersion,
    ],
  )

  const actionsValue = useMemo(
    (): ContentPreviewActions => ({
      getItems,
      getVersionedItems,
      getOpenItems,
      getOpenItemsBeforeSubthreadChange,
      updateItem,
      openItem,
      closeItem,
      closeItems,
      removeItems,
      openPreviewPane,
      closePreviewPane,
      openResourcePanel,
      closeResourcePanel,
      enableLoadingState,
      disableLoadingState,
      setOpenItemsBeforeSubthreadChange,
      contentPreviewRef,
      returnFocusElementRef,
    }),
    // All values here are stable (useCallback with empty deps, useStableCallback, or refs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEditedIssueWithSessionStorage(value)
  useSyncEditedItemsWithCurrentReferences(value)

  return (
    <ContentPreviewActionsContext value={actionsValue}>
      <ContentPreviewContext value={value}>{children}</ContentPreviewContext>
    </ContentPreviewActionsContext>
  )
}

const useEditedIssueWithSessionStorage = ({updateItem}: ContentPreviewContext) => {
  const {messagesLoading, selectedThreadID} = useChatState()
  const storageKeyPrefix = `edited-${selectedThreadID}`

  useEffect(() => {
    const editedIssues = getSessionStorageItemsForKeyPrefix<DraftIssue>(storageKeyPrefix)
    if (editedIssues && messagesLoading.state === 'loaded') {
      for (const editedIssue of editedIssues) {
        if (editedIssue) {
          updateItem(editedIssue)
        }
      }
    }
  }, [storageKeyPrefix, updateItem, messagesLoading.state])
}

export function useContentPreview(): ContentPreviewContext {
  const context = use(ContentPreviewContext)
  if (!context) {
    throw new Error('useContentPreview must be used within ContentPreviewProvider')
  }
  return context
}

/**
 * Returns stable callbacks and imperative getters for content preview.
 * Does not cause re-renders when items, openItems, or selectedItem change.
 * Use this when you only need to call actions or read data imperatively (not in render).
 */
export function useContentPreviewActions(): ContentPreviewActions {
  const context = use(ContentPreviewActionsContext)
  if (!context) {
    throw new Error('useContentPreviewActions must be used within ContentPreviewProvider')
  }
  return context
}

/**
 * Returns true if the content preview pane is open, false otherwise.
 */
export function useContentPreviewOpen() {
  const context = use(ContentPreviewContext)
  if (!context) {
    throw new Error('useContentPreviewOpen must be used within ContentPreviewProvider')
  }
  return useObservedState(context.isContentPreviewOpen)
}

/**
 * Returns true if the content preview pane is open, false otherwise.
 */
export function useResourcePanelOpen() {
  const context = use(ContentPreviewContext)
  if (!context) {
    throw new Error('useResourcePanelOpen must be used within ContentPreviewProvider')
  }
  return useObservedState(context.isResourcePanelOpen)
}
